/* eslint-disable max-lines-per-function */
import ConstructorRecommendationsService from '../../../services/constructor-recommendations-service';
import {SearchCache, SearchState} from '../../util';
import {DEFAULT_SECTION} from './constants';
import {constructorio, getBrowseResults} from './session';
import {transformRecommendationsRequest} from './transform-recommendations-request';
import {transformRecommendationsResponse} from './transform-recommendations-response';
import {transformSearchRequest} from './transform-search-request';
import {transformSearchResponse} from './transform-search-response';
import {transformSuggestRequest} from './transform-suggest-request';
import {transformSuggestResponse} from './transform-suggest-response';

class BaseClient {
	constructor () {

		/**
		 * @type {SearchCache}
		 */
		this.cache = new SearchCache();
	}
}

export class SearchClient extends BaseClient {

	/**
	 * @param {import('custom-extensions/search/lib/api/types').SearchRequest} defaultSearchParameters
	 */
	constructor (defaultSearchParameters, allowRedirects = false) {
		super();

		/**
		 * @type {HTMLDivElement}
		 */
		this.bus = document.createElement('div');

		/**
		 * @type {string}
		 */
		this.event = 'search';

		/**
		 * Allowed events
		 * @type {string[]}
		 */
		this.events = [this.event];

		/**
		 * Allow redirects
		 * @type {boolean}
		 */
		this.allowRedirects = allowRedirects;

		/**
		 * @type {SearchState}
		 */
		this.state = new SearchState(defaultSearchParameters);

		this.stateSetFromFunction = false;
	}

	/**
	 * @param {import('@constructor-io/constructorio-client-javascript').SearchResponseType} preloadedResponse The raw Constructor response envelope (`{response, result_id, request}`) -- same shape `search()` receives from the SDK
	 * @param {object} [rawRequest] The raw request Constructor received, for the `cnstrc-data` tracking script -- falls back to `preloadedResponse.request`
	 */
	setPreloadedResponse (preloadedResponse, rawRequest) {
		const transformedRequest = transformSearchRequest(this.state.output);
		return this.#postResponse(preloadedResponse?.response, transformedRequest, true, void 0, preloadedResponse?.result_id, rawRequest ?? preloadedResponse?.request, false);
	}

	on (event, callback) {
		if (!this.events.includes(event)) {
			throw new Error(`Constructor :: SearchClient - Event "${event}" does not exist.`);
		}

		this.bus.addEventListener(event, callback, false);
	}

	off (event, callback) {
		if (!this.events.includes(event)) {
			throw new Error(`Constructor :: SearchClient - Event "${event}" does not exist.`);
		}

		this.bus.removeEventListener(event, callback, false);
	}

	lock () {
		this.state.lock();

		return this;
	}

	/**
	 * @param {string} category
	 * @param {boolean} resetPage
	 */
	category (category, resetPage) {
		if (resetPage) {
			this.state.page(1);
		}

		this.state.category(category);

		return this;
	}

	/**
	 * @param {string} searchQuery
	 * @param {boolean} resetPage
	 */
	query (searchQuery, resetPage = true) {
		if (resetPage) {
			this.state.page(1);
		}

		this.state.query(searchQuery);

		return this;
	}

	/**
	 * @param {string} key
	 * @param {any} value
	 */
	other (key, value) {
		this.state.other(key, value);

		return this;
	}

	reset () {
		this.state.reset();

		return this;
	}

	/**
	 * @param {boolean} resetPage
	 */
	clearFacets (resetPage = true) {
		if (resetPage) {
			this.state.page(1);
		}

		this.state.clearFacets();

		return this;
	}

	/**
	 * @param {string} field
	 */
	clearFacet (field) {
		this.state.clearFacet(field);

		return this;
	}

	/**
	 * @param {number} resultsPerPage
	 */
	perPage (resultsPerPage, resetPage = true) {
		if (resetPage) {
			this.state.page(1);
		}

		this.state.perPage(resultsPerPage);

		return this;
	}

	/**
	 * @param {number} pageNumber
	 */
	page (pageNumber) {
		this.state.page(pageNumber);

		return this;
	}

	/**
	 * @param {string} field
	 * @param {'asc' | 'desc'} direction
	 */
	sort (field, direction) {
		this.state.sort(field, direction);

		return this;
	}

	/**
	 * @param {string} field
	 * @param {string | number[]} value
	 * @param {boolean} resetPage
	 */
	filter (field, value, resetPage = true) {
		if (resetPage) {
			this.state.page(1);
		}

		this.state.toggleFilter(field, value, false);

		return this;
	}

	/**
	 * @param {string} field
	 * @param {string | number[]} value
	 * @param {boolean} resetPage
	 */
	backgroundFilter (field, value, resetPage = true) {
		if (resetPage) {
			this.state.page(1);
		}

		this.state.toggleFilter(field, value, true);

		return this;
	}

	/**
	 * @param {string | any} queryStringOrObject
	 */
	setState (queryStringOrObject) {
		this.state.setStateFromQueryStringOrObject(queryStringOrObject);

		this.stateSetFromFunction = true;

		return this;
	}

	/**
	 * @param {import('custom-extensions/search/lib/api/types').SearchReturnValue} request
	 * @param {boolean} reconcileState Whether this response is authoritative enough to reconcile `this.state.filters` against (see `setPreloadedResponse`)
	 */
	afterSearch (request, reconcileState = true) {
		// Dispatch event and return
		this.bus.dispatchEvent(new CustomEvent(this.event, {
			detail: request
		}));

		// Fix state for first response after state set from function
		if (reconcileState && this.stateSetFromFunction) {
			this.stateSetFromFunction = false;

			// Reset filters
			this.state.filters = this.state.filters.filter(filter => filter.background);

			// Re-add filters to the state from the summary
			if (request.response.data && request.response.data.filterSummary && request.response.data.filterSummary.length > 0) {
				for (let filter of request.response.data.filterSummary) {
					this.filter(filter.field, typeof filter.value === 'object' ? [
						filter.value.rangeLow,
						filter.value.rangeHigh
					] : filter.value);
				}
			}
		}

		return this;
	}

	#processRedirect (url) {
		if (typeof url !== 'string' || !url.trim()) {
			return false;
		}

		try {
			const redirectURL = new URL(url, window.location.origin).toString();
			window.location.replace(redirectURL);
			return true;
		} catch (err) {
			return false;
		}
	}

	/**
	 * @param {import('@constructor-io/constructorio-client-javascript').SearchResponseType} response
	 * @param {import('./transform-search-request').TransformedSearchRequest} transformedRequest
	 * @param {boolean} cache
	 * @param {string} [error]
	 * @param {boolean} [reconcileState] Forwarded to `afterSearch` -- see `setPreloadedResponse`
	 * @returns {import('custom-extensions/search/lib/api/types').SearchReturnValue}
	 */
	// eslint-disable-next-line max-params
	#postResponse (response, transformedRequest, cache = false, error, resultId, rawRequest, reconcileState = true) {
		const transformedResponse = transformSearchResponse(response, transformedRequest, resultId, rawRequest);

		/**
		 * @type {import('custom-extensions/search/lib/api/types').SearchReturnValue}
		 */
		const returnValue = {
			requestParams: this.state.queryStringObject,
			requestQueryString: this.state.queryString,
			response: {
				cache,
				data: transformedResponse,
				status: error ? 400 : 200
			},
			transformedRequest
		};

		this.afterSearch(returnValue, reconcileState);

		return returnValue;
	}

	async search () {
		const mergedRequest = this.state.output;

		// Attempt to access cached record and return it
		const cachedReturnValue = this.cache.access(mergedRequest);
		if (cachedReturnValue) {
			this.afterSearch(cachedReturnValue);

			return cachedReturnValue;
		}

		const transformedRequest = transformSearchRequest(mergedRequest);

		const response = await (
			transformedRequest.isBrowse
				? getBrowseResults(transformedRequest.filterName,transformedRequest.filterValue, transformedRequest.parameters)
				: constructorio.search.getSearchResults(transformedRequest.term, transformedRequest.parameters)
		).catch(error => error);

		let error = '';
		if (response instanceof Error) {
			error = response.message;
		}

		if (this.allowRedirects && response?.response?.redirect?.data?.url) {
			const wasRedirected = this.#processRedirect(response.response.redirect.data.url);

			if (wasRedirected) {
				return true;
			}
		}

		const returnValue = this.#postResponse(response?.response, transformedRequest, false, error, response?.result_id, response?.request);

		this.cache.insert(mergedRequest, returnValue);

		return returnValue;
	}

}

export class AutocompleteClient extends SearchClient {
	constructor (defaultSearchParameters) {
		super(defaultSearchParameters);

		/**
		 * @type {string}
		 */
		this.event = 'autocomplete';

		/**
		 * @type {string}
		 */
		this.suggestEvent = 'suggest';

		/**
		 * Allowed events
		 * @type {string[]}
		 */
		this.events = [
			this.event,
			this.suggestEvent
		];
	}

	/**
	 * @returns {import('custom-extensions/search/lib/api/types').SuggestionReturnValue}
	 */
	async suggest () {
		const transformedRequest = transformSuggestRequest({
			...this.state.output,
			search: this.state.params.q,
			size: 5
		});

		const response = await constructorio.autocomplete
			.getAutocompleteResults(transformedRequest.query, transformedRequest.parameters)
			.catch(error => error);

		let error = '';
		if (response instanceof Error) {
			error = response.message;
		}

		const returnValue = {
			response: {
				data: error ? {
					products: [],
					terms: []
				} : transformSuggestResponse(response),
				status: error ? 400 : 200
			}
		};

		this.bus.dispatchEvent(new CustomEvent(this.suggestEvent, {
			detail: returnValue
		}));

		return returnValue;
	}
}

export class EventClient {

	/**
	 * NEEDS VERIFICATION: Constructor's `tracker` module exposes
	 * purpose-specific methods (e.g. `trackSearchResultClick`,
	 * `trackConversion`) rather than Cimulate's single generic
	 * `createEvent(name, data)` call -- this generic passthrough is a
	 * starting point, not a confirmed 1:1 replacement. Whoever wires up
	 * `Event.js`'s call sites should map each existing `eventName` to the
	 * matching purpose-built tracker method instead, where one exists.
	 */
	async create (eventName, eventData = {}) {
		await constructorio.tracker.trackGenericResultClick?.(eventName, eventData);
	}

}

export class RecommendationsClient {

	/**
	 * @type {ConstructorRecommendationsService}
	 */
	service = new ConstructorRecommendationsService();

	/**
	 * Fetch a single recommendations pod.
	 *
	 * @param {import('./transform-recommendations-request').RecommendationsRequest} request
	 * @returns {Promise<import('./transform-recommendations-response').TransformedRecommendationsResponse>}
	 */
	async get (request = {}) {
		const transformedRequest = transformRecommendationsRequest(request);

		if (!transformedRequest.podId) {
			// eslint-disable-next-line no-console
			console.warn('RecommendationsClient - a pod id is required.');

			return transformRecommendationsResponse({}, transformedRequest);
		}

		const response = await constructorio.recommendations
			.getRecommendations(transformedRequest.podId, transformedRequest.parameters)
			.catch(error => error);

		if (response instanceof Error) {
			// eslint-disable-next-line no-console
			console.warn(`RecommendationsClient - pod "${transformedRequest.podId}" failed: ${response.message}`);

			return transformRecommendationsResponse({}, transformedRequest);
		}

		return transformRecommendationsResponse(response, transformedRequest);
	}

	/**
	 * Transform pod items into full Miva product objects.
	 *
	 * @param {import('./transform-recommendations-response').RecommendationItem[]} items
	 * @param {number} limit
	 * @param {Record<string, string>} params
	 * @param {string} [recommendationType]
	 */
	async getData (items, limit, params, recommendationType) {
		const response = await this.service
			.recommendationsDataRequest(items, limit, params, recommendationType)
			.catch(() => ({}));

		return response;
	}

	/**
	 * Report a pod impression.
	 *
	 * @param {import('./transform-recommendations-response').RecommendationsMeta} meta
	 * @param {import('./transform-recommendations-response').RecommendationItem[]} items
	 * @param {string} [section]
	 */
	trackView (meta, items = [], section = DEFAULT_SECTION) {
		if (!meta?.podId || !items.length) {
			return;
		}

		constructorio.tracker.trackRecommendationView({
			items: items.map(item => {
				return {
					itemId: item.id,
					itemName: item.name,
					...typeof item.price === 'undefined' ? {} : {price: item.price}
				};
			}),
			numResultsViewed: items.length,
			podId: meta.podId,
			resultCount: meta.totalNumResults,
			resultId: meta.resultId,
			section,
			url: window.location.href
		});
	}

	/**
	 * Report a click on a pod item.
	 *
	 * @param {import('./transform-recommendations-response').RecommendationsMeta} meta
	 * @param {{id: string, name: string, position: number, strategyId?: string}} item
	 * @param {string} [section]
	 */
	trackClick (meta, item, section = DEFAULT_SECTION) {
		if (!meta?.podId || !item?.id) {
			return;
		}

		constructorio.tracker.trackRecommendationClick({
			itemId: item.id,
			itemName: item.name,
			numResultsPerPage: meta.numResults,
			podId: meta.podId,
			resultCount: meta.totalNumResults,
			resultId: meta.resultId,
			resultPositionOnPage: item.position,
			section,
			seedItemIds: meta.seedItemIds,
			strategyId: item.strategyId || meta.strategyId
		});
	}
}
